import { navForRole } from '@econdominio/core/nav';
import { type ReactNode, useEffect, useState } from 'react';
import { getJson } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Alert, Button } from '../components/ui';
import { navigate } from '../router';
import { ThemeSwitcher } from '../theme/ThemeSwitcher';
import styles from './screens.module.css';

interface Branding {
  nomeExibicao: string | null;
  corPrimaria: string | null;
  logoUrl: string | null;
}

/**
 * Shell do app do tenant. O menu inferior vem de `navForRole(papel)` — fonte única
 * em `@econdominio/core/nav`; `title` vazio esconde o cabeçalho da página.
 */
export function AppLayout({
  pathname,
  title,
  children,
}: {
  pathname: string;
  title: string;
  children: ReactNode;
}) {
  const { user, signOut } = useAuth();
  const [branding, setBranding] = useState<Branding | null>(null);

  useEffect(() => {
    void getJson<Branding>('/api/branding').then((b) => {
      setBranding(b);
      // Branding por sessão (ADR-006): tenant pode sobrescrever a cor da marca.
      if (b?.corPrimaria && /^#[0-9a-f]{6}$/i.test(b.corPrimaria)) {
        document.documentElement.style.setProperty('--brand', b.corPrimaria);
        document.documentElement.style.setProperty('--brand-strong', b.corPrimaria);
      }
    });
  }, []);

  const menu = (user && navForRole(user.papel, new Set(user.modulos))) ?? [];
  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <button
          type="button"
          className={styles.brand}
          onClick={() => navigate('/')}
          style={{ border: 'none', background: 'transparent' }}
        >
          {branding?.logoUrl ? (
            <img className={styles.brandLogo} src={branding.logoUrl} alt="" />
          ) : null}
          {branding?.nomeExibicao ?? 'eCondomínio'}
        </button>
        <div className={styles.topbarActions}>
          <ThemeSwitcher />
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            Sair
          </Button>
        </div>
      </header>

      <main className={styles.content}>
        {user?.suspenso ? (
          <Alert kind="warning">Condomínio suspenso — somente leitura</Alert>
        ) : null}
        {pathname !== '/' && title ? (
          <div className={styles.pageHead}>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              ← Início
            </Button>
            <h1 style={{ margin: 0, fontSize: 'var(--step-2)' }}>{title}</h1>
          </div>
        ) : null}
        {children}
      </main>

      <nav className={styles.bottomnav} aria-label="Navegação principal">
        {menu.map((item) => (
          <button
            key={item.id}
            type="button"
            className={styles.navItem}
            aria-current={pathname === item.path ? 'page' : undefined}
            onClick={() => navigate(item.path)}
          >
            <span className={styles.navIcon} aria-hidden="true">
              {item.icon}
            </span>
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
